import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X, AlertTriangle } from "lucide-react";

const METHODS = [
  { key: "phone", label: "Phone call" },
  { key: "url", label: "Booking URL" },
  { key: "email", label: "Email" },
];

function StockPicker({ onPick }) {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => { api.get("/stock-photos").then(r => setItems(r.data.items)).catch(()=>{}); }, []);
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <div className="text-xs text-[#5F6C60] mb-2">Or pick a stock photo (free to use):</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {items.map((p, i) => (
          <button key={`${p.url}-${i}`} type="button" data-testid={`stock-${i}`} onClick={()=>onPick(p.url)} className="aspect-square rounded-lg overflow-hidden border border-[#E8E2D6] hover:ring-2 hover:ring-[#0F766E] transition">
            <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

const SUBSTANTIVE_FIELDS = ["title", "description", "category", "original_price", "deal_price", "duration_minutes", "city", "state", "zip_code"];

export default function EditDeal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const isAdmin = user?.role === "admin";
  const editPath = isAdmin ? `/admin/deals/${id}` : `/my/deals/${id}`;

  useEffect(() => {
    api.get("/deals/categories").then(r => setCategories(r.data.categories));
    // Try owner endpoint first; admins fall back to public GET
    (async () => {
      try {
        let deal = null;
        if (isAdmin) {
          const { data } = await api.get(`/deals/${id}`);
          deal = data;
        } else {
          const { data } = await api.get("/my/deals");
          deal = data.items.find(d => d.id === id);
        }
        if (!deal) { setNotFound(true); return; }
        const f = {
          title: deal.title || "",
          description: deal.description || "",
          category: deal.category || "",
          original_price: String(deal.original_price ?? ""),
          deal_price: String(deal.deal_price ?? ""),
          duration_minutes: String(deal.duration_minutes ?? "60"),
          city: deal.city || "",
          state: deal.state || "",
          zip_code: deal.zip_code || "",
          image_url: deal.image_url || "",
          redemption_methods: deal.redemption_methods || ["phone"],
          contact_phone: deal.contact_phone || "",
          contact_email: deal.contact_email || "",
          booking_url: deal.booking_url || "",
        };
        setInitial({ ...f, status: deal.status });
        setForm(f);
      } catch {
        setNotFound(true);
      }
    })();
  }, [id, isAdmin]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif-display text-3xl tracking-tight">Deal not found</h1>
        <p className="text-sm text-[#5F6C60] mt-3">It may have been deleted or you don't have permission to edit it.</p>
        <Link to={isAdmin ? "/admin" : "/dashboard"} className="inline-block mt-6 text-[#D97757] hover:underline">Back</Link>
      </div>
    );
  }

  if (!form) {
    return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#5F6C60]">Loading…</div>;
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const toggleMethod = (key) => {
    const set = new Set(form.redemption_methods);
    if (set.has(key)) set.delete(key); else set.add(key);
    setForm({ ...form, redemption_methods: Array.from(set) });
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("JPEG, PNG, or WEBP only."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/deal-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isSubstantiveChange = () => {
    if (!initial) return false;
    if (isAdmin) return false;
    if (!["trial", "active"].includes(initial.status)) return false;
    return SUBSTANTIVE_FIELDS.some(k => {
      const a = k === "original_price" || k === "deal_price" ? parseFloat(form[k]) : (k === "duration_minutes" ? parseInt(form[k], 10) : form[k]);
      const b = initial[k];
      const bVal = typeof b === "number" ? b : (k === "original_price" || k === "deal_price" ? parseFloat(b) : (k === "duration_minutes" ? parseInt(b, 10) : b));
      return a !== bVal;
    });
  };
  const willReReview = isSubstantiveChange();

  const submit = async (e) => {
    e.preventDefault();
    if (willReReview && !window.confirm("Substantial edits (title, price, description, or location) will hide this deal from public until our team re-approves it (usually within 24h). Continue?")) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        original_price: parseFloat(form.original_price),
        deal_price: parseFloat(form.deal_price),
        duration_minutes: parseInt(form.duration_minutes, 10),
      };
      const { data } = await api.patch(editPath, payload);
      if (data?.re_review) toast.success("Saved — your edits are queued for re-review.");
      else toast.success("Saved");
      navigate(isAdmin ? "/admin" : "/dashboard");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not save deal");
    } finally { setLoading(false); }
  };

  const f = "mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12";

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Edit deal</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">{form.title || "Untitled"}</h1>
      <p className="text-[#5F6C60] mt-2">Make changes below. {isAdmin ? "As admin, no re-review is triggered." : "Changes to title, price, description, or location trigger a quick admin re-review."}</p>

      {willReReview && (
        <div data-testid="edit-warning" className="mt-6 bg-[#D4A373]/15 border-l-4 border-[#D4A373] rounded-r-xl p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-[#8b6a3f] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-bold text-sm text-[#2C352D]">This will trigger re-review</div>
            <p className="text-sm text-[#5F6C60] mt-1">Your deal will be hidden from public listings until our team re-approves it. Your paid window is preserved.</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 bg-white p-8 rounded-2xl border border-[#E8E2D6] space-y-6">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em]">Deal title</Label>
          <Input data-testid="edit-title" required value={form.title} onChange={update("title")} className={f} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em]">Description</Label>
          <Textarea data-testid="edit-description" required value={form.description} onChange={update("description")} rows={4} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Category</Label>
            <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
              <SelectTrigger data-testid="edit-category" className={f + " h-12"}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">Duration (minutes)</Label><Input data-testid="edit-duration" type="number" required min="15" value={form.duration_minutes} onChange={update("duration_minutes")} className={f}/></div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">Original price ($)</Label><Input data-testid="edit-original-price" type="number" step="0.01" required value={form.original_price} onChange={update("original_price")} className={f}/></div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">Deal price ($)</Label><Input data-testid="edit-deal-price" type="number" step="0.01" required value={form.deal_price} onChange={update("deal_price")} className={f}/></div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">City</Label><Input data-testid="edit-city" required value={form.city} onChange={update("city")} className={f}/></div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">State</Label><Input data-testid="edit-state" required maxLength={2} value={form.state} onChange={update("state")} className={f}/></div>
          <div><Label className="text-xs font-bold uppercase tracking-[0.15em]">ZIP code</Label><Input data-testid="edit-zip" required value={form.zip_code} onChange={update("zip_code")} className={f}/></div>

          <div className="sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Deal photo</Label>
            {form.image_url ? (
              <div className="mt-3 relative inline-block">
                <img src={form.image_url} alt="Preview" className="w-full max-w-sm h-48 object-cover rounded-xl border border-[#E8E2D6]" />
                <button data-testid="clear-image" type="button" onClick={()=>setForm({...form, image_url: ""})} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 border border-[#E8E2D6] shadow-sm"><X className="w-4 h-4 text-[#5F6C60]"/></button>
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <input ref={fileInputRef} data-testid="edit-image-file" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden"/>
                <Button data-testid="edit-upload-btn" type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="rounded-full"><Upload className="w-4 h-4 mr-2"/> {uploading ? "Uploading…" : "Upload photo"}</Button>
                <span className="text-xs text-[#5F6C60]">JPEG/PNG/WEBP · up to 5 MB</span>
              </div>
            )}
            <StockPicker onPick={(url)=>setForm({...form, image_url: url})} />
          </div>
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em]">How can customers redeem?</Label>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {METHODS.map(m => (
              <label key={m.key} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${form.redemption_methods.includes(m.key) ? 'border-[#D97757] bg-[#D97757]/5' : 'border-[#E8E2D6] bg-[#F9F7F3]'}`}>
                <Checkbox data-testid={`edit-method-${m.key}`} checked={form.redemption_methods.includes(m.key)} onCheckedChange={()=>toggleMethod(m.key)}/>
                <span className="text-sm font-medium">{m.label}</span>
              </label>
            ))}
          </div>
          {form.redemption_methods.includes("phone") && <div className="mt-4"><Label className="text-xs font-bold uppercase tracking-[0.15em]">Contact phone</Label><Input data-testid="edit-contact-phone" required value={form.contact_phone} onChange={update("contact_phone")} className={f}/></div>}
          {form.redemption_methods.includes("email") && <div className="mt-4"><Label className="text-xs font-bold uppercase tracking-[0.15em]">Contact email</Label><Input data-testid="edit-contact-email" type="email" required value={form.contact_email} onChange={update("contact_email")} className={f}/></div>}
          {form.redemption_methods.includes("url") && <div className="mt-4"><Label className="text-xs font-bold uppercase tracking-[0.15em]">Booking URL</Label><Input data-testid="edit-booking-url" required value={form.booking_url} onChange={update("booking_url")} className={f}/></div>}
        </div>

        <div className="flex gap-3">
          <Button data-testid="edit-cancel" type="button" variant="outline" className="rounded-full" onClick={()=>navigate(isAdmin ? "/admin" : "/dashboard")}>Cancel</Button>
          <Button data-testid="edit-submit" disabled={loading} type="submit" className="flex-1 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white h-12">{loading ? "Saving…" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}
